
import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartJSTooltip,
  Legend,
  ArcElement,
  ChartType,
  ChartData,
  ChartOptions,
} from "chart.js";
import { Line, Bar, Pie, Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartJSTooltip,
  Legend
);

// Define specific chart types we're using
type SupportedChartType = 'line' | 'bar' | 'pie' | 'doughnut';

// Define proper types for chart data
interface ChartDataset {
  label?: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  [key: string]: any;
}

interface ChartDataType {
  labels?: unknown[];
  datasets: ChartDataset[];
}

interface ChartProps {
  type: SupportedChartType;
  data: ChartDataType;
  options?: any; // Using 'any' for options to avoid type incompatibilities
  className?: string;
}

export function Chart({ type, data, options, className }: ChartProps) {
  const renderChart = () => {
    switch (type) {
      case "line":
        return <Line data={data as ChartData<'line'>} options={options} className={className} />;
      case "bar":
        return <Bar data={data as ChartData<'bar'>} options={options} className={className} />;
      case "pie":
        return <Pie data={data as ChartData<'pie'>} options={options} className={className} />;
      case "doughnut":
        return <Doughnut data={data as ChartData<'doughnut'>} options={options} className={className} />;
      default:
        return <Bar data={data as ChartData<'bar'>} options={options} className={className} />;
    }
  };

  return renderChart();
}

interface ChartContainerProps {
  config: Record<string, { color: string }>;
  className?: string;
  children: React.ReactNode;
}

export function ChartContainer({ config, className, children }: ChartContainerProps) {
  // Create CSS variables for colors from config
  const style = Object.entries(config).reduce((acc, [key, { color }]) => {
    return {
      ...acc,
      [`--color-${key}`]: color,
    };
  }, {});

  return (
    <div className={className} style={style as React.CSSProperties}>
      {children}
    </div>
  );
}

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

export function ChartTooltipContent({ active, payload, label }: ChartTooltipContentProps) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white p-2 shadow-md border rounded-md">
      <p className="text-sm font-medium">{label}</p>
      <div className="mt-1">
        {payload.map((entry, index) => (
          <div key={`item-${index}`} className="flex items-center text-xs">
            <span
              className="w-2 h-2 rounded-full mr-1"
              style={{ backgroundColor: entry.color }}
            />
            <span className="font-medium">{entry.name}: </span>
            <span className="ml-1">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Export the ChartJSTooltip as ChartTooltip for backward compatibility
export const ChartTooltip = ChartJSTooltip;
